import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, RefreshCw, Calendar, BarChart3, TrendingUp } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { LoadingState, ErrorState } from '@/components/ui/loading-state'
import { cn } from '@/lib/utils'
import { useConsumablesReport } from '@/hooks/useFinanceApi'
import type { ConsumablesViewType, EventConsumptionRow } from '@/api/types'

const VIEW_TYPES: { value: ConsumablesViewType; label: string; icon: React.ReactNode }[] = [
  { value: 'DATE',  label: 'Date Wise',   icon: <Calendar className="h-4 w-4" /> },
  { value: 'MONTH', label: 'Month Wise',  icon: <BarChart3 className="h-4 w-4" /> },
  { value: 'YTD',   label: 'YTD',         icon: <TrendingUp className="h-4 w-4" /> },
]

const CHANNEL_OPTIONS = [
  { value: 'ALL',          label: 'All Channels' },
  { value: 'EMAIL',        label: 'Email' },
  { value: 'SMS',          label: 'SMS' },
  { value: 'WHATSAPP',     label: 'WhatsApp' },
  { value: 'RCS',          label: 'RCS' },
  { value: 'WEB_PUSH',     label: 'Web Push' },
  { value: 'MOBILE_PUSH',  label: 'Mobile Push' },
]

const CHANNEL_COLORS: Record<string, string> = {
  EMAIL:        'text-blue-600',
  SMS:          'text-green-600',
  WHATSAPP:     'text-emerald-600',
  RCS:          'text-purple-600',
  WEB_PUSH:     'text-orange-600',
  MOBILE_PUSH:  'text-pink-600',
}

function formatNum(n: number): string {
  if (n >= 1_000_000_000) return (n / 1_000_000_000).toFixed(2) + 'B'
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(2) + 'M'
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K'
  return n.toLocaleString()
}

function getDefaultDateRange(viewType: ConsumablesViewType) {
  const now = new Date()
  if (viewType === 'DATE') {
    const start = new Date(now)
    start.setDate(start.getDate() - 6)
    return {
      startDate: start.toISOString().slice(0, 10),
      endDate: now.toISOString().slice(0, 10),
    }
  }
  if (viewType === 'MONTH') {
    return {
      startDate: `${now.getFullYear()}-01-01`,
      endDate: now.toISOString().slice(0, 10),
    }
  }
  return { startDate: undefined, endDate: undefined }
}

function formatPeriodLabel(period: string, viewType: ConsumablesViewType): string {
  if (viewType === 'DATE') {
    const d = new Date(period)
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })
  }
  if (viewType === 'MONTH') {
    const [y, m] = period.split('-')
    const d = new Date(parseInt(y), parseInt(m) - 1, 1)
    return d.toLocaleDateString('en-IN', { month: 'short', year: '2-digit' })
  }
  return period
}

function SummaryCard({ label, value, colorClass }: { label: string; value: number; colorClass: string }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="text-xs text-gray-500 font-medium mb-1">{label}</div>
      <div className={cn('text-xl font-bold', colorClass)}>{formatNum(value)}</div>
    </div>
  )
}

export function ConsumablesReportPage() {
  const [viewType, setViewType] = useState<ConsumablesViewType>('MONTH')
  const [channel, setChannel] = useState('ALL')

  const now = new Date()
  const defaultRange = getDefaultDateRange('MONTH')
  const [startDate, setStartDate] = useState(defaultRange.startDate ?? '')
  const [endDate, setEndDate] = useState(defaultRange.endDate ?? '')
  const [year] = useState(now.getFullYear())

  const reportParams = useMemo(() => ({
    viewType,
    channel: channel === 'ALL' ? undefined : channel,
    startDate: viewType !== 'YTD' ? startDate || undefined : undefined,
    endDate: viewType !== 'YTD' ? endDate || undefined : undefined,
    year: viewType === 'YTD' ? year : undefined,
  }), [viewType, channel, startDate, endDate, year])

  const { data: report, loading, error, refetch } = useConsumablesReport(reportParams)

  // Build pivot: account → periods
  const pivot = useMemo(() => {
    if (!report?.data) return { accountRows: [], periods: [] }
    const periodsSet = new Set<string>()
    const accountMap = new Map<number, { accountName: string; byPeriod: Map<string, EventConsumptionRow> }>()

    report.data.forEach((row) => {
      periodsSet.add(row.period)
      if (!accountMap.has(row.accId)) {
        accountMap.set(row.accId, { accountName: row.accountName, byPeriod: new Map() })
      }
      accountMap.get(row.accId)!.byPeriod.set(row.period, row)
    })

    const periods = Array.from(periodsSet).sort()
    const accountRows = Array.from(accountMap.entries()).map(([accId, { accountName, byPeriod }]) => ({
      accId,
      accountName,
      byPeriod,
    }))

    return { accountRows, periods }
  }, [report])

  // Grand totals per channel across all data
  const totals = useMemo(() => {
    const rows = report?.data ?? []
    return {
      email: rows.reduce((s, r) => s + r.emailOut, 0),
      sms: rows.reduce((s, r) => s + r.smsOut, 0),
      whatsapp: rows.reduce((s, r) => s + r.whatsappOut, 0),
      rcs: rows.reduce((s, r) => s + r.rcsOut, 0),
      webPush: rows.reduce((s, r) => s + r.webPushOut, 0),
      mobilePush: rows.reduce((s, r) => s + r.mobilePushOut, 0),
      total: rows.reduce((s, r) => s + r.totalOut, 0),
    }
  }, [report])

  function getCellValue(row: EventConsumptionRow): number {
    if (channel === 'EMAIL') return row.emailOut
    if (channel === 'SMS') return row.smsOut
    if (channel === 'WHATSAPP') return row.whatsappOut
    if (channel === 'RCS') return row.rcsOut
    if (channel === 'WEB_PUSH') return row.webPushOut
    if (channel === 'MOBILE_PUSH') return row.mobilePushOut
    return row.totalOut
  }

  const handleViewTypeChange = (vt: ConsumablesViewType) => {
    setViewType(vt)
    const range = getDefaultDateRange(vt)
    setStartDate(range.startDate ?? '')
    setEndDate(range.endDate ?? '')
  }

  return (
    <div className="p-6 space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/finance" className="text-gray-400 hover:text-gray-600 transition-colors">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Consumables Report</h1>
            <p className="text-sm text-gray-500 mt-0.5">Account-wise outbound event consumption</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={refetch}>
            <RefreshCw className="h-4 w-4 mr-1.5" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
        <SummaryCard label="Email"       value={totals.email}       colorClass={CHANNEL_COLORS.EMAIL} />
        <SummaryCard label="SMS"         value={totals.sms}         colorClass={CHANNEL_COLORS.SMS} />
        <SummaryCard label="WhatsApp"    value={totals.whatsapp}    colorClass={CHANNEL_COLORS.WHATSAPP} />
        <SummaryCard label="RCS"         value={totals.rcs}         colorClass={CHANNEL_COLORS.RCS} />
        <SummaryCard label="Web Push"    value={totals.webPush}     colorClass={CHANNEL_COLORS.WEB_PUSH} />
        <SummaryCard label="Mobile Push" value={totals.mobilePush}  colorClass={CHANNEL_COLORS.MOBILE_PUSH} />
        <SummaryCard label="Total"       value={totals.total}       colorClass="text-gray-900" />
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4 pb-4">
          <div className="flex flex-wrap gap-4 items-end">
            {/* View type */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">View</label>
              <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                {VIEW_TYPES.map(({ value, label, icon }) => (
                  <button
                    key={value}
                    onClick={() => handleViewTypeChange(value)}
                    className={cn(
                      'flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors',
                      viewType === value
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-50'
                    )}
                  >
                    {icon}
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Channel */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">Channel</label>
              <div className="flex flex-wrap gap-1.5">
                {CHANNEL_OPTIONS.map(({ value, label }) => (
                  <button
                    key={value}
                    onClick={() => setChannel(value)}
                    className={cn(
                      'px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors',
                      channel === value
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Date range for DATE / MONTH views */}
            {viewType !== 'YTD' && (
              <div className="flex gap-2 items-end">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">
                    {viewType === 'DATE' ? 'Start Date' : 'Start Month'}
                  </label>
                  <input
                    type={viewType === 'DATE' ? 'date' : 'month'}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={viewType === 'MONTH' ? startDate.slice(0, 7) : startDate}
                    onChange={(e) => {
                      const v = e.target.value
                      setStartDate(viewType === 'MONTH' ? `${v}-01` : v)
                    }}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">
                    {viewType === 'DATE' ? 'End Date' : 'End Month'}
                  </label>
                  <input
                    type={viewType === 'DATE' ? 'date' : 'month'}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={viewType === 'MONTH' ? endDate.slice(0, 7) : endDate}
                    onChange={(e) => {
                      const v = e.target.value
                      if (viewType === 'MONTH') {
                        const [y, m] = v.split('-').map(Number)
                        const lastDay = new Date(y, m, 0).getDate()
                        setEndDate(`${v}-${String(lastDay).padStart(2, '0')}`)
                      } else {
                        setEndDate(v)
                      }
                    }}
                  />
                </div>
              </div>
            )}

            {viewType === 'YTD' && (
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">Year</label>
                <div className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-gray-50">
                  {year}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Report table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            Account Wise Consumables —{' '}
            {CHANNEL_OPTIONS.find((c) => c.value === channel)?.label},{' '}
            {VIEW_TYPES.find((v) => v.value === viewType)?.label}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8">
              <LoadingState message="Loading consumables report…" />
            </div>
          ) : error ? (
            <div className="p-8">
              <ErrorState message={error.message} onRetry={refetch} />
            </div>
          ) : !report || pivot.accountRows.length === 0 ? (
            <div className="p-12 text-center text-gray-500 text-sm">
              No data for the selected period.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="text-left px-4 py-3 font-medium text-gray-600 whitespace-nowrap sticky left-0 bg-gray-50 z-10">
                      Account
                    </th>
                    {channel === 'ALL' ? (
                      <>
                        <th className={cn('px-4 py-3 font-medium text-right whitespace-nowrap', CHANNEL_COLORS.EMAIL)}>Email</th>
                        <th className={cn('px-4 py-3 font-medium text-right whitespace-nowrap', CHANNEL_COLORS.SMS)}>SMS</th>
                        <th className={cn('px-4 py-3 font-medium text-right whitespace-nowrap', CHANNEL_COLORS.WHATSAPP)}>WhatsApp</th>
                        <th className={cn('px-4 py-3 font-medium text-right whitespace-nowrap', CHANNEL_COLORS.RCS)}>RCS</th>
                        <th className={cn('px-4 py-3 font-medium text-right whitespace-nowrap', CHANNEL_COLORS.WEB_PUSH)}>Web Push</th>
                        <th className={cn('px-4 py-3 font-medium text-right whitespace-nowrap', CHANNEL_COLORS.MOBILE_PUSH)}>Mobile Push</th>
                        <th className="px-4 py-3 font-medium text-right whitespace-nowrap text-gray-700">Total</th>
                      </>
                    ) : viewType !== 'YTD' ? (
                      pivot.periods.map((p) => (
                        <th key={p} className="px-4 py-3 font-medium text-right whitespace-nowrap text-gray-600">
                          {formatPeriodLabel(p, viewType)}
                        </th>
                      ))
                    ) : (
                      <th className="px-4 py-3 font-medium text-right whitespace-nowrap text-gray-700">
                        YTD {year}
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {pivot.accountRows.map(({ accId, accountName, byPeriod }, idx) => {
                    const rows = Array.from(byPeriod.values())
                    const accTotal = rows.reduce((s, r) => s + getCellValue(r), 0)

                    return (
                      <tr
                        key={accId}
                        className={cn(
                          'border-b border-gray-100 hover:bg-gray-50 transition-colors',
                          idx % 2 === 0 ? '' : 'bg-gray-50/50'
                        )}
                      >
                        <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap sticky left-0 bg-white z-10">
                          <div className="text-sm">{accountName}</div>
                          <div className="text-xs text-gray-400">#{accId}</div>
                        </td>

                        {channel === 'ALL' ? (
                          <>
                            {/* For ALL channel in YTD, aggregate across all periods */}
                            {['emailOut','smsOut','whatsappOut','rcsOut','webPushOut','mobilePushOut','totalOut'].map((field) => {
                              const sum = rows.reduce((s, r) => s + (r as unknown as Record<string, number>)[field], 0)
                              return (
                                <td key={field} className="px-4 py-3 text-right font-mono text-gray-700 whitespace-nowrap">
                                  {formatNum(sum)}
                                </td>
                              )
                            })}
                          </>
                        ) : viewType !== 'YTD' ? (
                          <>
                            {pivot.periods.map((p) => {
                              const row = byPeriod.get(p)
                              return (
                                <td key={p} className="px-4 py-3 text-right font-mono text-gray-700 whitespace-nowrap">
                                  {row ? formatNum(getCellValue(row)) : '—'}
                                </td>
                              )
                            })}
                          </>
                        ) : (
                          <td className="px-4 py-3 text-right font-mono font-medium text-gray-900 whitespace-nowrap">
                            {formatNum(accTotal)}
                          </td>
                        )}
                      </tr>
                    )
                  })}
                </tbody>

                {/* Grand total row */}
                <tfoot>
                  <tr className="border-t-2 border-gray-300 bg-gray-100 font-semibold">
                    <td className="px-4 py-3 text-gray-900 sticky left-0 bg-gray-100 z-10">Grand Total</td>
                    {channel === 'ALL' ? (
                      <>
                        <td className="px-4 py-3 text-right font-mono text-blue-700">{formatNum(totals.email)}</td>
                        <td className="px-4 py-3 text-right font-mono text-green-700">{formatNum(totals.sms)}</td>
                        <td className="px-4 py-3 text-right font-mono text-emerald-700">{formatNum(totals.whatsapp)}</td>
                        <td className="px-4 py-3 text-right font-mono text-purple-700">{formatNum(totals.rcs)}</td>
                        <td className="px-4 py-3 text-right font-mono text-orange-700">{formatNum(totals.webPush)}</td>
                        <td className="px-4 py-3 text-right font-mono text-pink-700">{formatNum(totals.mobilePush)}</td>
                        <td className="px-4 py-3 text-right font-mono text-gray-900">{formatNum(totals.total)}</td>
                      </>
                    ) : viewType !== 'YTD' ? (
                      pivot.periods.map((p) => {
                        const colTotal = pivot.accountRows.reduce((s, { byPeriod }) => {
                          const row = byPeriod.get(p)
                          return s + (row ? getCellValue(row) : 0)
                        }, 0)
                        return (
                          <td key={p} className="px-4 py-3 text-right font-mono text-gray-900">
                            {formatNum(colTotal)}
                          </td>
                        )
                      })
                    ) : (
                      <td className="px-4 py-3 text-right font-mono text-gray-900">
                        {formatNum(totals.total)}
                      </td>
                    )}
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

    </div>
  )
}
